import React, { useState } from 'react';

import { useGenerateAriaId } from '@lumapps/aria-ids/hooks/useGenerateAriaId';
import { background, classnames, useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { notificationCircle } from '@lumapps/lumx/custom-icons';
import { mdiClose } from '@lumapps/lumx/icons';
import {
    Alignment,
    ColorPalette,
    ColorVariant,
    Emphasis,
    FlexBox,
    IconButton,
    ListItem,
    Orientation,
    Size,
    Text,
} from '@lumapps/lumx/react';
import { useSelector } from '@lumapps/redux/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { User } from '@lumapps/user/types';
import { getElapsedTimeFromUtcDate } from '@lumapps/utils/time/getElapsedTimeFromUtcDate';

import { DATA_NAMESPACE } from '../../constants';
import { isNotificationsBetaEnabled } from '../../ducks/selectors';
import { useDescription } from '../../hooks/useDescription';
import { useNotificationRoute } from '../../hooks/useNotificationRoute';
import { NOTIFICATIONS_CENTER } from '../../keys';
import { Notification } from '../../types';
import { NotificationContextMenu } from './NotificationContextMenu';
import { NotificationListItemBefore } from './NotificationListItemBefore';

import './index.scss';

const CLASSNAME = 'notification-list-item';

export type NotificationListItemCommonProps = {
    notification: Notification;
    senderDetails?: User | null;
    setNotificationRead?(notificationId: string, isRead: boolean, shouldCloseDropdown?: boolean): void;
    deleteNotification?(notificationId: string, isNotificationRead: boolean | undefined): void;
    closeNotificationMenu?(): void;
    className?: string;
    notificationContextMenuClassName?: string;
    isChild?: boolean;
};

const NotificationListItemCommon: React.FC<NotificationListItemCommonProps> = ({
    notification,
    senderDetails,
    setNotificationRead,
    deleteNotification,
    closeNotificationMenu,
    className,
    notificationContextMenuClassName,
}) => {
    const { block, element } = useClassnames(CLASSNAME);
    const { get } = useDataAttributes(DATA_NAMESPACE);
    const description = useDescription(notification, senderDetails);
    const { linkProps, route, linkAs: LinkAs = 'div' } = useNotificationRoute(notification);
    const updatedFrom = getElapsedTimeFromUtcDate(notification.updatedAt);
    const [isNotificationHover, setIsNotificationHover] = useState(false);
    const { translateKey } = useTranslate();
    const listItemRef = React.useRef<HTMLLIElement>(null);
    const isNotificationsEnabled = useSelector(isNotificationsBetaEnabled);
    /**
     * Id of this row's description, used to give the row's action buttons a per-row context
     * (their labels alone are identical from one notification to the next).
     */
    const descriptionId = useGenerateAriaId('description', { prefix: 'notification-description' });

    const onClick = React.useCallback(
        (evt: React.MouseEvent) => {
            evt.stopPropagation();

            if (route) {
                // setNotificationRead will also close the notification menu
                if (!notification.isRead && setNotificationRead) {
                    setNotificationRead(notification.uid, true);
                }
                // if the notification is already read we just want to close the notification menu
                if (closeNotificationMenu) {
                    closeNotificationMenu();
                }
            }
        },
        [notification.uid, setNotificationRead, closeNotificationMenu, route, notification.isRead],
    );

    const notificationLinkProps = { ...linkProps, onClick: route ? onClick : undefined };

    const handleMarkAs = React.useCallback(
        (isRead: boolean) => () => {
            if (setNotificationRead) {
                // Find next notification link before item is removed
                const nextLink =
                    listItemRef.current?.nextElementSibling?.querySelector<HTMLElement>('a') ||
                    listItemRef.current?.previousElementSibling?.querySelector<HTMLElement>('a');

                setNotificationRead(notification.uid, isRead, false);

                // Focus next notification link after DOM updates
                if (nextLink) {
                    requestAnimationFrame(() => nextLink.focus());
                }
            }
        },
        [notification.uid, setNotificationRead],
    );

    const handleDelete = React.useCallback(() => {
        if (deleteNotification) {
            // Find next notification link before item is removed
            const nextLink =
                listItemRef.current?.nextElementSibling?.querySelector<HTMLElement>('a') ||
                listItemRef.current?.previousElementSibling?.querySelector<HTMLElement>('a');

            deleteNotification(notification.uid, notification.isRead);

            // Focus next notification link after DOM updates
            if (nextLink) {
                requestAnimationFrame(() => nextLink.focus());
            }
        }
    }, [notification.uid, notification.isRead, deleteNotification]);

    // NEW UI (feature flag ON): Inline delete + mark as read/unread buttons
    if (isNotificationsEnabled) {
        return (
            <ListItem
                ref={listItemRef}
                className={classnames(
                    // TODO: temporary ishighlighted, isSelected don't work for now with ListItem.Action
                    block({ 'hovered-unread': !notification.isRead && isNotificationHover }, [
                        !notification.isRead && background(ColorPalette.primary, ColorVariant.L6),
                    ]),
                    className,
                )}
                size={Size.huge}
                before={
                    <NotificationListItemBefore
                        className={element('before')}
                        notification={notification}
                        senderDetails={senderDetails}
                    />
                }
                after={
                    <FlexBox
                        orientation={Orientation.horizontal}
                        hAlign={Alignment.center}
                        vAlign={Alignment.right}
                        gap={Size.tiny}
                        noShrink
                    >
                        <IconButton
                            className={element('action-button', { delete: true })}
                            icon={mdiClose}
                            label={translateKey(GLOBAL.DELETE)}
                            emphasis={Emphasis.low}
                            onClick={handleDelete}
                            aria-describedby={descriptionId}
                            {...get({ element: 'delete', type: notification.uid })}
                        />
                        {notification.isRead ? (
                            <IconButton
                                icon={notificationCircle}
                                color={ColorPalette.grey}
                                emphasis={Emphasis.low}
                                label={translateKey(NOTIFICATIONS_CENTER.MARK_AS_UNREAD)}
                                onClick={handleMarkAs(false)}
                                aria-describedby={descriptionId}
                                {...get({ element: 'mark-as-unread', type: notification.uid })}
                            />
                        ) : (
                            <IconButton
                                icon={notificationCircle}
                                color={ColorPalette.primary}
                                emphasis={Emphasis.low}
                                onClick={handleMarkAs(true)}
                                label={translateKey(NOTIFICATIONS_CENTER.MARK_AS_READ)}
                                aria-describedby={descriptionId}
                                {...get({ element: 'mark-as-read', type: notification.uid })}
                            />
                        )}
                    </FlexBox>
                }
                {...get({ element: 'row', type: notification.type })}
            >
                <ListItem.Action as={LinkAs} {...notificationLinkProps}>
                    <Text as="span" id={descriptionId}>
                        {description}
                    </Text>
                    <Text as="p" color={ColorPalette.dark} colorVariant={ColorVariant.L2}>
                        {updatedFrom}
                    </Text>
                </ListItem.Action>
            </ListItem>
        );
    }

    // OLD UI (feature flag OFF): Context menu with 3-dot icon
    return (
        <ListItem
            className={classnames(
                block({
                    clickable: Boolean(route),
                }),
                className,
            )}
            size={Size.tiny}
            onMouseLeave={() => setIsNotificationHover(false)}
            onMouseEnter={() => setIsNotificationHover(true)}
            {...get({ element: 'row', type: notification.type })}
        >
            <FlexBox orientation={Orientation.horizontal} vAlign={Alignment.spaceBetween} hAlign={Alignment.center}>
                <LinkAs className={element('content')} {...notificationLinkProps}>
                    <FlexBox orientation={Orientation.horizontal} vAlign={Alignment.center} gap={Size.huge}>
                        <NotificationListItemBefore
                            className={element('before')}
                            notification={notification}
                            senderDetails={senderDetails}
                        />
                        <FlexBox>
                            <div id={descriptionId}>{description}</div>

                            <Text as="p" color={ColorPalette.dark} colorVariant={ColorVariant.L2}>
                                {updatedFrom}
                            </Text>
                        </FlexBox>
                    </FlexBox>
                </LinkAs>
                <div className={element('menu')}>
                    <NotificationContextMenu
                        className={notificationContextMenuClassName}
                        isRead={notification.isRead}
                        showCircle={!notification.isRead && !isNotificationHover}
                        notificationId={notification.uid}
                        describedBy={descriptionId}
                        onMarkAs={handleMarkAs}
                        onDelete={handleDelete}
                    />
                </div>
            </FlexBox>
        </ListItem>
    );
};

NotificationListItemCommon.displayName = 'ListItem';

export { NotificationListItemCommon };
